/**
 * Brand-aware product search scoring.
 *
 * Handles queries that combine brand + product name even when the brand
 * is not part of the product title (e.g. "Nike Air Max 90" matching a
 * product named "Air Max 90" with brand "Nike").
 *
 * Lower score = better match. Returns null when there is no meaningful match.
 */

export interface SearchableProduct {
  name: string | null;
  sku: string | null;
  brand: string | null;
}

/** Normalize: lowercase, strip punctuation, collapse whitespace. */
export function normalizeSearchText(input: string): string {
  return (input ?? "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .replace(/[^\p{L}\p{N}\s]+/gu, " ") // strip punctuation
    .replace(/\s+/g, " ")
    .trim();
}

function tokenize(input: string): string[] {
  const norm = normalizeSearchText(input);
  return norm ? norm.split(" ") : [];
}

/** Levenshtein distance, capped for short tokens only. */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const m = a.length;
  const n = b.length;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

/** Allowed typos based on token length: 0 for ≤3 chars, 1 for ≤6, else 2. */
function maxTypos(token: string): number {
  if (token.length <= 3) return 0;
  if (token.length <= 6) return 1;
  return 2;
}

/** True if `token` matches any word in `text` exactly, by prefix, or fuzzily. */
function tokenMatchesText(token: string, textWords: string[]): "exact" | "prefix" | "fuzzy" | null {
  if (!token || textWords.length === 0) return null;
  for (const w of textWords) {
    if (w === token) return "exact";
  }
  for (const w of textWords) {
    if (w.startsWith(token) || token.startsWith(w)) return "prefix";
  }
  const allowed = maxTypos(token);
  if (allowed === 0) return null;
  for (const w of textWords) {
    // Skip very different lengths to keep cost down
    if (Math.abs(w.length - token.length) > allowed) continue;
    if (levenshtein(w, token) <= allowed) return "fuzzy";
  }
  return null;
}

/**
 * Score a product against a query.
 *
 * Scoring tiers (lower is better):
 *   0  — exact SKU match
 *   1  — SKU prefix match
 *   2  — SKU contains query
 *   3  — exact "brand + name" match
 *   4  — query starts "brand + name"
 *   5  — name starts with query (after stripping leading brand)
 *   6  — name contains query
 *   10+ — token-based match; bonus for matching brand token
 */
export function scoreProductMatch(
  product: SearchableProduct,
  rawQuery: string,
): number | null {
  const query = normalizeSearchText(rawQuery);
  if (!query) return null;

  const name = normalizeSearchText(product.name ?? "");
  const brand = normalizeSearchText(product.brand ?? "");
  const sku = (product.sku ?? "").toLowerCase().trim();
  const rawQueryLower = rawQuery.toLowerCase().trim();

  // SKU matches use the raw query (SKUs may contain punctuation/numbers).
  if (sku) {
    if (sku === rawQueryLower) return 0;
    if (sku.startsWith(rawQueryLower)) return 1;
    if (sku.includes(rawQueryLower)) return 2;
  }

  const combined = brand && name ? `${brand} ${name}` : name || brand;

  // Strip the brand prefix from the name for "name only" comparisons,
  // so "Air Max 90" still matches when user types "Air Max 90".
  const nameWithoutBrand =
    brand && name.startsWith(brand + " ") ? name.slice(brand.length + 1) : name;

  if (combined === query) return 3;
  if (combined.startsWith(query)) return 4;
  if (nameWithoutBrand.startsWith(query) || name.startsWith(query)) return 5;
  if (name.includes(query) || combined.includes(query)) return 6;

  // Token-based matching: every query token must match somewhere
  // in the brand or product name (exact / prefix / fuzzy).
  const queryTokens = tokenize(query);
  if (queryTokens.length === 0) return null;

  const nameWords = name ? name.split(" ") : [];
  const brandWords = brand ? brand.split(" ") : [];
  const allWords = [...brandWords, ...nameWords];

  let totalPenalty = 10;
  let matchedBrandToken = false;

  for (const tok of queryTokens) {
    const kind = tokenMatchesText(tok, allWords);
    if (!kind) return null; // every token must match

    if (brandWords.length > 0 && tokenMatchesText(tok, brandWords)) {
      matchedBrandToken = true;
    }

    if (kind === "exact") totalPenalty += 0;
    else if (kind === "prefix") totalPenalty += 1;
    else totalPenalty += 3; // fuzzy
  }

  // Bonus when the query references the brand — boosts brand+name queries.
  if (matchedBrandToken) totalPenalty -= 2;

  return totalPenalty;
}
